import { initializeAnalytics, isSupported, logEvent, setConsent, setDefaultEventParameters, setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { app, measurementId } from './firebase-config';
import { canonicalAppsPath } from '../screens/apps/routes';

const CONSENT_KEY = 'keval-analytics-consent-v1';
const ADS_DENIED = { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' };
const TITLES = {
  '/': "Keval's Portfolio",
  '/cv': "Keval's Resume and CV",
  '/apps': 'Portfolio · Keval Langalia',
  '/apps/birdify': 'Birdify · Bird identification on your device · Keval Langalia',
  '/apps/birdify/privacy': 'Birdify privacy policy · Keval Langalia',
  '/apps/numbcrunch': 'NumbCrunch · Number deduction on Android · Keval Langalia',
  '/apps/numbcrunch/privacy': 'NumbCrunch privacy policy · Keval Langalia',
  '/apps/qr-code': 'QR code generator · Portfolio · Keval Langalia',
  '/privacy': 'Website privacy · Keval Langalia',
  '/blog': 'Blog · Keval Langalia',
  '/blog/article': 'Article · Blog · Keval Langalia',
};
const browser = typeof window !== 'undefined';
const debug = browser && ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)
  && process.env.REACT_APP_ANALYTICS_DEBUG === 'true';
const enabled = browser && Boolean(measurementId) && (debug || (process.env.NODE_ENV === 'production'
  && ['keval.io', 'www.keval.io'].includes(window.location.hostname)));
let analytics = null;
let initializing = null;
let consent = readConsent();
let consentRevision = 0;
let currentPage = null;
let sentPage = null;
let currentRoute = null;
let ownerWorkspaceActive = false;
function decodedPath(path) {
  try { return decodeURIComponent(path); } catch { return null; }
}
// Router matches decoded segments. Unknown malformed escapes also suspend collection.
const isAdmin = (path) => decodedPath(path) === null || /^\/(?:admin|blog\/admin)(?:\/|$)/i.test(decodedPath(path));
const isArticle = (path) => /^\/blog\/[a-z0-9]+(?:-[a-z0-9]+)*\/?$/.test(decodedPath(path) || '');
let suspended = browser && (isAdmin(window.location.pathname) || isArticle(window.location.pathname));

function readConsent() {
  try {
    const value = window.localStorage.getItem(CONSENT_KEY);
    return ['granted', 'denied'].includes(value) ? value : null;
  } catch {
    return null;
  }
}

export function getAnalyticsConsent() {
  return consent;
}

// Only public campaign labels, never arbitrary query values or visitor identifiers.
export function readCampaign(search) {
  const query = new URLSearchParams(search);
  const campaign = {};
  for (const name of ['source', 'medium', 'campaign', 'content', 'term', 'id']) {
    const value = query.get(`utm_${name}`);
    if (value && /^[a-z0-9_.~-]{1,100}$/i.test(value)) {
      campaign[`campaign_${name === 'campaign' ? 'name' : name}`] = value;
    }
  }
  return campaign;
}

function referralOrigin(value) {
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? `${url.origin}/` : '';
  } catch {
    return '';
  }
}

// Keep the landing attribution in memory while the visitor reads the consent notice.
// It survives SPA navigation, but is neither persisted nor sent before consent.
let landingCampaign = browser && !suspended ? readCampaign(window.location.search) : {};
let landingReferrer = browser && !suspended ? referralOrigin(document.referrer) : '';

function pageParameters(pathname) {
  // Unknown URLs can contain personal data; only known public routes are reported.
  const canonicalPath = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  const path = Object.hasOwn(TITLES, canonicalPath) ? canonicalPath : '/not-found';
  return {
    page_path: path,
    page_location: `https://keval.io${path}`,
    page_title: TITLES[path] || 'Page not found · Keval Langalia',
  };
}

function collectionAllowed() {
  return enabled && consent === 'granted' && !suspended;
}

function disableCollection(disabled) {
  if (browser && measurementId) window[`ga-disable-${measurementId}`] = disabled;
  if (analytics) {
    try { setAnalyticsCollectionEnabled(analytics, !disabled); } catch { /* Optional analytics. */ }
  }
}

function clearAnalyticsCookies() {
  const hostParts = window.location.hostname.split('.');
  const domains = ['', ...hostParts.map((_, index) => hostParts.slice(index).join('.'))];
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (name !== '_ga' && !name.startsWith('_ga_')) return;
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax${domain ? `; domain=${domain}` : ''}`;
    });
  });
}

async function startAnalytics() {
  if (!collectionAllowed()) return null;
  if (analytics) return analytics;
  if (!initializing) {
    initializing = (async () => {
      try {
        if (!(await isSupported()) || !collectionAllowed()) return null;
        // No Google tag, Firebase installation or measurement requests before this point.
        setConsent({ ...ADS_DENIED, analytics_storage: 'granted' });
        disableCollection(false);
        analytics = initializeAnalytics(app, {
          config: {
            send_page_view: false,
            allow_google_signals: false,
            allow_ad_personalization_signals: false,
            cookie_expires: 60 * 60 * 24 * 365,
            cookie_path: '/',
            ...landingCampaign,
            ...(debug ? { debug_mode: true } : {}),
          },
        });
        // The SDK installs its command queue synchronously, before its async tag load.
        // Set defaults now so an older cached default cannot overwrite later SPA pages.
        setDefaultEventParameters({ ...currentPage, page_referrer: landingReferrer });
        return analytics;
      } catch {
        return null;
      }
    })().finally(() => { initializing = null; });
  }
  return initializing;
}

async function sendCurrentPage() {
  const page = currentPage;
  if (!page) return false;
  const revision = consentRevision;
  const instance = await startAnalytics();
  if (!instance || !collectionAllowed() || revision !== consentRevision || page !== currentPage || page === sentPage) return false;
  const params = { ...page, page_referrer: sentPage?.page_location || landingReferrer };
  try {
    // Also update automatic engagement events, so their URL/title never falls back to raw input.
    setDefaultEventParameters({ ...params, ...landingCampaign });
    logEvent(instance, 'page_view', params);
    sentPage = page;
    return true;
  } catch {
    return false;
  }
}

// The public list becomes a private workspace while its owner controls are visible.
export function setOwnerWorkspaceActive(active) {
  if (ownerWorkspaceActive === active) return;
  ownerWorkspaceActive = active;
  const route = currentRoute || (browser ? window.location.pathname : '/blog');
  currentRoute = null;
  setCurrentPage(route);
}

export function setCurrentPage(pathname) {
  if (currentRoute === pathname) return;
  currentRoute = pathname;
  consentRevision += 1;
  // Suspend before React's passive route effects or pending SDK initialization.
  suspended = ownerWorkspaceActive || isAdmin(pathname) || isArticle(pathname);
  if (suspended) {
    currentPage = null;
    sentPage = null;
    landingCampaign = {};
    landingReferrer = '';
    disableCollection(true);
    if (analytics) {
      try {
        setConsent({ ...ADS_DENIED, analytics_storage: 'denied' });
        setDefaultEventParameters({ page_path: '', page_location: '', page_title: '', page_referrer: '',
          campaign_source: '', campaign_medium: '', campaign_name: '', campaign_content: '', campaign_term: '', campaign_id: '' });
      } catch { /* Optional analytics. */ }
    }
    return;
  }
  const page = pageParameters(pathname);
  // Preserve alias deduplication for known public paths.
  if (currentPage?.page_path === page.page_path) return;
  currentPage = page;
  applyConsent();
}

/** Suspend at the router's synchronous location change, before React renders private UI. */
export function observeAnalyticsRoutes(router) {
  const update = ({ location }) => setCurrentPage(canonicalAppsPath(location.pathname) || location.pathname);
  update(router.state);
  return router.subscribe(update);
}

/** An async reader may resume collection only for the still-current public article. */
export function confirmBlogArticle(pathname) {
  if (ownerWorkspaceActive || isAdmin(pathname) || currentRoute !== pathname || !isArticle(pathname) || !suspended) return;
  suspended = false;
  currentPage = pageParameters('/blog/article');
  applyConsent();
}

export function setAnalyticsConsent(value) {
  if (!['granted', 'denied'].includes(value)) return;
  consent = value;
  consentRevision += 1;
  try { window.localStorage.setItem(CONSENT_KEY, value); } catch { /* This visit still respects the choice. */ }
  applyConsent();
}

function applyConsent() {
  disableCollection(!collectionAllowed());
  if (analytics) {
    try { setConsent({ ...ADS_DENIED, analytics_storage: collectionAllowed() ? 'granted' : 'denied' }); } catch { /* Optional analytics. */ }
  }
  if (consent !== 'granted') {
    sentPage = null;
    try { clearAnalyticsCookies(); } catch { /* Restricted cookie access must not break the site. */ }
  } else {
    void sendCurrentPage();
  }
  window.dispatchEvent(new Event('analytics-consent-change'));
}

if (browser) {
  disableCollection(!collectionAllowed());
  window.addEventListener('storage', (event) => {
    if (event.key !== CONSENT_KEY && event.key !== null) return;
    consent = readConsent();
    consentRevision += 1;
    applyConsent();
  });
}

/** Optional telemetry never blocks navigation. Events before consent are discarded. */
export function trackEvent(eventName, eventParams = {}) {
  if (!collectionAllowed() || !analytics || !sentPage) return false;
  try {
    logEvent(analytics, eventName, { ...currentPage, ...eventParams });
    return true;
  } catch {
    return false;
  }
}

/** Existing stable element IDs are retained; DOM input and full link URLs are never sent. */
export function trackClick(page, element) {
  return (event) => {
    if (event?.currentTarget?.isConnected === false) return false;
    const params = { page, element };
    let name = 'navigation_click';
    if (element === 'download_resume_link') {
      name = 'file_download';
      params.file_name = 'keval_resume.pdf';
      params.file_extension = 'pdf';
    } else if (element === 'qr_generate_button') name = 'qr_generate';
    else if (element === 'qr_download_button') name = 'qr_download';
    else if (element.includes('email')) name = 'contact_click';
    else if (/project|card_title|details_button/.test(element)) {
      name = 'select_content';
      params.content_type = 'project';
      params.content_id = element;
    } else {
      const href = event?.currentTarget?.getAttribute?.('href');
      if (href) {
        try {
          const url = new URL(href, window.location.origin);
          if (['https:', 'http:'].includes(url.protocol) && !['keval.io', 'www.keval.io', window.location.hostname].includes(url.hostname)) {
            name = 'outbound_click';
            params.link_domain = url.hostname;
          }
        } catch { /* A malformed link must not interrupt navigation. */ }
      }
    }
    return trackEvent(name, params);
  };
}
